import { useEffect } from 'react'
import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { FONTS, SPACING, RADIUS, COLORS } from '@/lib/brand'
import { useSemanticColors } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'
import { TIERED_FEE_POLICY } from '@/lib/utils/constants'
import { nextGuestServiceFeePct, pairFeeOrdinal } from '@/lib/utils/tiered-fee'
import { productTelemetryService } from '@/lib/services/product-telemetry.service'
import { RETENTION_EVENTS } from '@/lib/retention/events'

interface RepeatPairFeeCardProps {
  completedPairTrips: number
  feePct: number
  compact?: boolean
  testID?: string
}

export function RepeatPairFeeCard({
  completedPairTrips,
  feePct,
  compact = false,
  testID = 'retention.pair-fee',
}: RepeatPairFeeCardProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const ordinal = pairFeeOrdinal(completedPairTrips)
  const nextPct = nextGuestServiceFeePct(completedPairTrips, TIERED_FEE_POLICY)

  useEffect(() => {
    productTelemetryService.recordEvent('booking_step', RETENTION_EVENTS.pairFeeQuoted, {
      elementId: testID,
      metadata: { feePct, completedPairTrips, nextPct },
    })
  }, [completedPairTrips, feePct, nextPct, testID])

  const steps = TIERED_FEE_POLICY.tiers

  return (
    <View
      testID={testID}
      accessibilityRole="text"
      accessibilityLabel={t(
        ordinal === 1 ? 'retention.pair_fee_first_title' : 'retention.pair_fee_repeat_title',
        { pct: feePct },
      )}
      style={{
        padding: compact ? SPACING.sm : SPACING.md,
        borderRadius: RADIUS.lg,
        borderWidth: 1,
        borderColor: colors.border,
        backgroundColor: colors.surface,
        gap: SPACING.sm,
      }}
    >
      <Text
        style={{
          fontSize: 12,
          fontFamily: FONTS.body,
          color: colors.textMuted,
          textTransform: 'uppercase',
          letterSpacing: 0.6,
        }}
      >
        {t(ordinal === 1 ? 'retention.pair_fee_first_kicker' : 'retention.pair_fee_repeat_kicker')}
      </Text>
      <Text
        style={{
          fontSize: 17,
          lineHeight: compact ? 22 : 24,
          fontFamily: FONTS.bodyBold,
          color: colors.textPrimary,
        }}
      >
        {ordinal === 1
          ? t('retention.pair_fee_first_title')
          : t('retention.pair_fee_repeat_title', { pct: feePct })}
      </Text>
      <View style={{ flexDirection: 'row', gap: SPACING.xs }}>
        {steps.map((tier, index) => {
          const active = feePct === tier.pct
          const labelKey =
            index === 0
              ? 'retention.tier_first'
              : index === 1
                ? 'retention.tier_second'
                : 'retention.tier_rest'
          return (
            <View
              key={tier.pct}
              style={{
                flex: 1,
                minHeight: 44,
                borderRadius: RADIUS.md,
                paddingVertical: SPACING.sm,
                paddingHorizontal: SPACING.xs,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: active ? COLORS.coral.DEFAULT : colors.background,
                borderWidth: active ? 0 : 1,
                borderColor: colors.border,
              }}
            >
              <Text
                numberOfLines={1}
                adjustsFontSizeToFit
                style={{
                  fontSize: 12,
                  fontFamily: FONTS.body,
                  color: active ? '#FFFFFF' : colors.textMuted,
                }}
              >
                {t(labelKey)}
              </Text>
              <Text
                numberOfLines={1}
                style={{
                  fontSize: 16,
                  fontFamily: FONTS.bodyBold,
                  color: active ? '#FFFFFF' : colors.textPrimary,
                }}
              >
                {tier.pct}%
              </Text>
            </View>
          )
        })}
      </View>
      <Text
        style={{
          fontSize: 14,
          lineHeight: 20,
          fontFamily: FONTS.body,
          color: colors.textSecondary,
        }}
      >
        {ordinal < 3
          ? t('retention.pair_fee_next', { pct: nextPct })
          : t('retention.pair_fee_floor')}
      </Text>
      <Text
        style={{
          fontSize: 13,
          lineHeight: 18,
          fontFamily: FONTS.body,
          color: colors.textMuted,
        }}
      >
        {t('retention.pair_fee_body')}
      </Text>
    </View>
  )
}
